'use client';

import { forwardRef } from 'react';
import { cn } from '@/core/utils/cn';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-hms-gold hover:bg-hms-goldLt text-black font-semibold',
  secondary: 'border border-hms-border text-hms-textSub hover:text-hms-text hover:border-hms-textDim bg-transparent',
  ghost: 'text-hms-textSub hover:text-hms-text hover:bg-white/5 bg-transparent',
  danger: 'bg-hms-red/10 text-hms-red hover:bg-hms-red/20 border border-hms-red/20',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[12px] rounded-lg gap-1.5',
  md: 'h-9 px-4 text-[13px] rounded-lg gap-2',
  lg: 'h-11 px-6 text-[14px] rounded-xl gap-2',
};

/** The Button look, for elements that are not <button> (see ButtonLink). */
export function buttonClasses(opts: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean; disabled?: boolean; className?: string }) {
  const { variant = 'primary', size = 'md', fullWidth, disabled, className } = opts;
  return cn(
    'inline-flex items-center justify-center font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-hms-gold/30',
    variantStyles[variant],
    sizeStyles[size],
    fullWidth && 'w-full',
    disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
    className
  );
}

export type { ButtonVariant, ButtonSize };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, fullWidth, leftIcon, rightIcon, disabled, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={buttonClasses({ variant, size, fullWidth, disabled: disabled || loading, className })}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : leftIcon}
        {children}
        {rightIcon}
      </button>
    );
  }
);
Button.displayName = 'Button';
